import { useEffect, useState, useCallback, useMemo } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../components/ui/sheet";
import { Eye, ExternalLink, Trash2, Edit3, Clock, AlertCircle } from "lucide-react";
import EmptyState from "../components/EmptyState";
import { formatEur, formatNumber } from "../lib/format";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const STAGES = [
    { key: "watch", label: "Watch" },
    { key: "request_info", label: "Request info" },
    { key: "contacted_broker", label: "Contacted broker" },
    { key: "due_diligence", label: "Due diligence" },
    { key: "offer_preparation", label: "Offer prep" },
    { key: "offer_sent", label: "Offer sent" },
    { key: "negotiation", label: "Negotiation" },
    { key: "rejected", label: "Rejected" },
    { key: "acquired", label: "Acquired" },
    { key: "ignore", label: "Ignore" },
];
const PRIORITY_STYLE = {
    urgent: "border-rose-500/40 text-rose-300",
    high: "border-orange-500/40 text-orange-300",
    medium: "border-blue-500/30 text-blue-300",
    low: "border-muted-foreground/30 text-muted-foreground",
};

const Watchlist = () => {
    const [pipeline, setPipeline] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("kanban");
    const [editing, setEditing] = useState(null); // watchlist item

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/watchlist/pipeline");
            setPipeline(data);
        } catch (e) {
            toast.error("Failed to load watchlist", { description: String(e) });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const removeItem = async (id) => {
        await api.delete(`/watchlist/${id}`);
        toast.success("Removed");
        await load();
    };

    if (loading || !pipeline) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-10 w-1/3" />
                <div className="grid grid-cols-4 gap-3">
                    {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-40" />)}
                </div>
            </div>
        );
    }

    if (pipeline.total_items === 0) {
        return (
            <div className="space-y-4">
                <Header pipeline={pipeline} view={view} setView={setView} />
                <EmptyState
                    icon={Eye}
                    title="No deals in pipeline yet"
                    description="Add listings to your watchlist from the Deal Finder or Investment Committee. Each added deal flows through the 10-stage pipeline from 'Watch' to 'Acquired'."
                />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="watchlist-page">
            <Header pipeline={pipeline} view={view} setView={setView} />

            {view === "kanban" ? (
                <KanbanView pipeline={pipeline} onEdit={setEditing} onRemove={removeItem} />
            ) : (
                <TableView pipeline={pipeline} onEdit={setEditing} onRemove={removeItem} />
            )}

            {editing && (
                <EditDrawer
                    item={editing}
                    onClose={() => setEditing(null)}
                    onSaved={async () => { setEditing(null); await load(); }}
                />
            )}
        </div>
    );
};

const Header = ({ pipeline, view, setView }) => (
    <header className="space-y-3">
        <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
                <h1 className="text-3xl sm:text-4xl font-serif">Watchlist Pipeline</h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                    Real deals only — every entry is a listing you added yourself. Move
                    deals through the 10-stage pipeline from Watch to Acquired (or Reject/Ignore).
                </p>
            </div>
            <div className="flex gap-2 items-center">
                <Button variant={view === "kanban" ? "default" : "outline"} size="sm" onClick={() => setView("kanban")} data-testid="view-kanban">Kanban</Button>
                <Button variant={view === "table" ? "default" : "outline"} size="sm" onClick={() => setView("table")} data-testid="view-table">Table</Button>
            </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="pipeline-kpi">
            <Stat label="Total deals" value={pipeline.total_items} />
            <Stat label="Capital required" value={formatEur(pipeline.total_capital_required_eur)} sub="excludes rejected/ignored" />
            <Stat label="In active stages" value={STAGES.filter((s) => !["rejected", "acquired", "ignore"].includes(s.key)).reduce((sum, s) => sum + (pipeline.summary[s.key]?.count || 0), 0)} />
            <Stat label="Overdue reminders" value={STAGES.reduce((sum, s) => sum + (pipeline.summary[s.key]?.overdue_count || 0), 0)} variant="warn" />
        </div>
    </header>
);

const KanbanView = ({ pipeline, onEdit, onRemove }) => (
    <div className="flex gap-3 overflow-x-auto pb-3" data-testid="kanban-board">
        {STAGES.map((s) => {
            const cards = pipeline.stages[s.key] || [];
            const info = pipeline.summary[s.key] || {};
            return (
                <div key={s.key} className="flex-shrink-0 w-72" data-testid={`kanban-col-${s.key}`}>
                    <div className="mb-2 flex items-center justify-between">
                        <h3 className="text-xs uppercase tracking-widest text-muted-foreground">{s.label}</h3>
                        <Badge variant="outline" className="text-[10px]">{info.count || 0}</Badge>
                    </div>
                    {info.capital_required_eur > 0 && (
                        <div className="text-[10px] text-muted-foreground mb-2">
                            {formatEur(info.capital_required_eur)} capital
                        </div>
                    )}
                    <div className="space-y-2 min-h-[60px]">
                        {cards.map((c) => <Card key={c.id} className="p-3 cursor-pointer hover:border-accent/40" onClick={() => onEdit(c)} data-testid={`kanban-card-${c.id}`}>
                            <div className="flex items-start justify-between gap-2 mb-1">
                                <Badge variant="outline" className={`text-[9px] ${PRIORITY_STYLE[c.priority] || ""}`}>{c.priority}</Badge>
                                {c.is_overdue && (
                                    <Clock className="w-3 h-3 text-rose-400" />
                                )}
                            </div>
                            <Link to={`/deals/${c.listing_id}`} onClick={(e) => e.stopPropagation()} className="text-[12px] font-medium line-clamp-2 hover:text-accent">
                                {c.title || "Untitled"}
                            </Link>
                            <div className="text-[10px] text-muted-foreground mt-1">{c.municipality}</div>
                            <div className="flex flex-wrap gap-x-2 mt-2 text-[10px] text-muted-foreground">
                                {c.asking_price_eur && <span>{formatEur(c.asking_price_eur)}</span>}
                                {c.flip_score != null && <span>flip {Math.round(c.flip_score)}</span>}
                                {c.target_buy_price_eur && <span className="text-accent">target {formatEur(c.target_buy_price_eur)}</span>}
                            </div>
                            {c.next_action && (
                                <div className="text-[10px] mt-2 pt-2 border-t border-border/40">
                                    <span className="text-muted-foreground">Next:</span> {c.next_action}
                                </div>
                            )}
                        </Card>)}
                        {cards.length === 0 && (
                            <div className="border border-dashed border-border/40 rounded-md p-4 text-center text-[10px] text-muted-foreground italic">
                                empty
                            </div>
                        )}
                    </div>
                </div>
            );
        })}
    </div>
);

const TableView = ({ pipeline, onEdit, onRemove }) => {
    const allItems = useMemo(() => {
        const out = [];
        for (const stage of STAGES) {
            for (const c of pipeline.stages[stage.key] || []) {
                out.push({ ...c, stage_key: stage.key, stage_label: stage.label });
            }
        }
        return out;
    }, [pipeline]);

    if (allItems.length === 0) return null;
    return (
        <Card className="p-0 overflow-hidden" data-testid="watchlist-table">
            <table className="w-full text-sm">
                <thead className="border-b border-border/60 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="py-2 px-3 text-left">Listing</th>
                        <th className="py-2 px-3 text-left">Stage</th>
                        <th className="py-2 px-3 text-left">Priority</th>
                        <th className="py-2 px-3 text-right">Asking</th>
                        <th className="py-2 px-3 text-right">Target</th>
                        <th className="py-2 px-3 text-right">Max</th>
                        <th className="py-2 px-3 text-left">Next action</th>
                        <th className="py-2 px-3 text-left">Reminder</th>
                        <th className="py-2 px-3"></th>
                    </tr>
                </thead>
                <tbody>
                    {allItems.map((c) => (
                        <tr key={c.id} className="border-b border-border/30 hover:bg-muted/20" data-testid={`watch-row-${c.id}`}>
                            <td className="py-2 px-3">
                                <Link to={`/deals/${c.listing_id}`} className="line-clamp-1 max-w-[260px] hover:text-accent">{c.title || "Untitled"}</Link>
                                <div className="text-[10px] text-muted-foreground">{c.municipality}</div>
                            </td>
                            <td className="py-2 px-3"><Badge variant="outline" className="text-[10px]">{c.stage_label}</Badge></td>
                            <td className="py-2 px-3"><Badge variant="outline" className={`text-[10px] ${PRIORITY_STYLE[c.priority] || ""}`}>{c.priority}</Badge></td>
                            <td className="py-2 px-3 text-right">{c.asking_price_eur ? formatEur(c.asking_price_eur) : "—"}</td>
                            <td className="py-2 px-3 text-right text-accent">{c.target_buy_price_eur ? formatEur(c.target_buy_price_eur) : "—"}</td>
                            <td className="py-2 px-3 text-right">{c.max_bid_eur ? formatEur(c.max_bid_eur) : "—"}</td>
                            <td className="py-2 px-3 text-[11px]">{c.next_action || "—"}</td>
                            <td className="py-2 px-3 text-[11px]">
                                {c.reminder_date ? (
                                    <span className={c.is_overdue ? "text-rose-400 font-medium" : ""}>{c.reminder_date}</span>
                                ) : "—"}
                            </td>
                            <td className="py-2 px-3 flex gap-1">
                                <Button size="sm" variant="ghost" onClick={() => onEdit(c)} data-testid={`edit-${c.id}`}><Edit3 className="w-3.5 h-3.5" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => onRemove(c.id)} className="text-rose-400 hover:text-rose-300"><Trash2 className="w-3.5 h-3.5" /></Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Card>
    );
};

const Stat = ({ label, value, sub, variant }) => (
    <Card className={`p-3 ${variant === "warn" ? "border-amber-500/30" : ""}`}>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="text-xl font-serif mt-1">{value}</div>
        {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
);

const EditDrawer = ({ item, onClose, onSaved }) => {
    const [form, setForm] = useState({
        stage: item.stage || "watch",
        priority: item.priority || "medium",
        target_buy_price_eur: item.target_buy_price_eur ?? "",
        max_bid_eur: item.max_bid_eur ?? "",
        walk_away_price_eur: item.walk_away_price_eur ?? "",
        broker_name: item.broker_name ?? "",
        broker_email: item.broker_email ?? "",
        broker_phone: item.broker_phone ?? "",
        next_action: item.next_action ?? "",
        reminder_date: item.reminder_date ?? "",
        due_diligence_status: item.due_diligence_status ?? "",
        user_decision_notes: item.user_decision_notes ?? "",
        notes: item.notes ?? "",
    });
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const body = {};
            for (const [k, v] of Object.entries(form)) {
                if (v === "" || v === null || v === undefined) continue;
                body[k] = ["target_buy_price_eur", "max_bid_eur", "walk_away_price_eur"].includes(k) ? Number(v) : v;
            }
            await api.put(`/watchlist/${item.id}`, body);
            toast.success("Updated");
            await onSaved();
        } catch (e) {
            toast.error("Save failed", { description: e?.response?.data?.detail || String(e) });
        } finally {
            setSaving(false);
        }
    };

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    return (
        <Sheet open onOpenChange={(o) => !o && onClose()}>
            <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="watch-edit-drawer">
                <SheetHeader>
                    <SheetTitle className="font-serif text-xl line-clamp-2">{item.title || "Untitled"}</SheetTitle>
                </SheetHeader>
                <div className="mt-5 space-y-3 text-sm">
                    <Field label="Stage">
                        <Select value={form.stage} onValueChange={(v) => set("stage", v)}>
                            <SelectTrigger data-testid="stage-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {STAGES.map((s) => <SelectItem key={s.key} value={s.key} data-testid={`stage-option-${s.key}`}>{s.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Priority">
                        <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                            <SelectTrigger data-testid="priority-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {["low", "medium", "high", "urgent"].map((p) => <SelectItem key={p} value={p} data-testid={`priority-option-${p}`}>{p}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <div className="grid grid-cols-3 gap-2">
                        <Field label="Target €"><Input type="number" value={form.target_buy_price_eur} onChange={(e) => set("target_buy_price_eur", e.target.value)} data-testid="target-bid" /></Field>
                        <Field label="Max bid €"><Input type="number" value={form.max_bid_eur} onChange={(e) => set("max_bid_eur", e.target.value)} data-testid="max-bid" /></Field>
                        <Field label="Walk away €"><Input type="number" value={form.walk_away_price_eur} onChange={(e) => set("walk_away_price_eur", e.target.value)} /></Field>
                    </div>
                    <Field label="Broker name"><Input value={form.broker_name} onChange={(e) => set("broker_name", e.target.value)} /></Field>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Broker email"><Input value={form.broker_email} onChange={(e) => set("broker_email", e.target.value)} /></Field>
                        <Field label="Broker phone"><Input value={form.broker_phone} onChange={(e) => set("broker_phone", e.target.value)} /></Field>
                    </div>
                    <Field label="Next action"><Input value={form.next_action} onChange={(e) => set("next_action", e.target.value)} placeholder="e.g. Call broker, request floor plan" /></Field>
                    <Field label="Reminder date"><Input type="date" value={form.reminder_date} onChange={(e) => set("reminder_date", e.target.value)} /></Field>
                    <Field label="Due diligence status">
                        <Select value={form.due_diligence_status || "not_started"} onValueChange={(v) => set("due_diligence_status", v)}>
                            <SelectTrigger><SelectValue placeholder="not_started" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="not_started">Not started</SelectItem>
                                <SelectItem value="in_progress">In progress</SelectItem>
                                <SelectItem value="complete">Complete</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Decision notes"><Textarea rows={3} value={form.user_decision_notes} onChange={(e) => set("user_decision_notes", e.target.value)} /></Field>
                    <Field label="General notes"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
                </div>
                <div className="mt-6 pt-4 border-t border-border/40 flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
                    <Button size="sm" onClick={save} disabled={saving} data-testid="save-watch-edit">Save</Button>
                </div>
            </SheetContent>
        </Sheet>
    );
};

const Field = ({ label, children }) => (
    <div>
        <label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">{label}</label>
        {children}
    </div>
);

export default Watchlist;
